import {
  convertReactFlowGroupToSocketGroup,
  convertReactFlowNodeToSocketNode,
} from "@features/builder/graph/editGraphSocketConverter"
import { AppNode, GroupNode } from "@features/builder/graph/types"
import { GraphDetails, GraphDocument } from "@features/builder/types"
import logger from "@utils/logger"
import * as Y from "yjs"

// Replace the "data" Y.Text, don't edit it in place. Y.Map.set is last-writer-wins, so two users
// saving at the same time end with one of their values. Editing the Y.Text (delete + insert) keeps
// both concurrent inserts and glues two JSON strings together.
// Readers must observe the "graph" map, not the Y.Text (see useGraphDocumentData).
export const updateGraphDocumentData = (doc: GraphDocument, data: GraphDetails) => {
  doc.getMap("graph").set("data", new Y.Text(JSON.stringify(data)))
}

export const updateGraphDocumentGroups = (doc: GraphDocument, reactFlowGroups: GroupNode[]) => {
  const yGroups = doc.getMap("graph").get("groups")!
  const socketGroups = reactFlowGroups.map(convertReactFlowGroupToSocketGroup)
  socketGroups.forEach((updatedGroup) => {
    const savedGroup = yGroups.get(updatedGroup.id)?.toJSON()
    const ug = JSON.stringify(updatedGroup)
    if (savedGroup == ug) return

    if (import.meta.env.VITE_SHOW_NODE_UPDATES === "true") {
      logger.info({ updatedGroup, savedGroup: JSON.parse(savedGroup ?? "null") })
    }

    yGroups.set(updatedGroup.id, new Y.Text(ug))
  })

  const socketGroupsIds = Object.keys(yGroups.toJSON())
  const reactFlowGroupsIds = reactFlowGroups.map((node) => node.id)
  const groupsIdsToDelete = socketGroupsIds.filter((id) => !reactFlowGroupsIds.includes(id))
  groupsIdsToDelete.forEach((id) => yGroups.delete(id))
}

export const updateGraphDocumentNodes = (
  doc: GraphDocument,
  reactFlowNodes: Exclude<AppNode, GroupNode>[],
) => {
  const yNodes = doc.getMap("graph").get("nodes")!
  const socketNodes = reactFlowNodes.map(convertReactFlowNodeToSocketNode)
  socketNodes.forEach((updatedNode) => {
    const savedNode = yNodes.get(updatedNode.id)?.toJSON()
    const un = JSON.stringify(updatedNode)
    if (un === savedNode) return

    if (import.meta.env.VITE_SHOW_NODE_UPDATES === "true") {
      logger.info({ updatedNode, savedNode: JSON.parse(savedNode ?? "null") })
    }

    yNodes.set(updatedNode.id, new Y.Text(un))
  })

  const socketNodesIds = Object.keys(yNodes.toJSON())
  const reactFlowNodesIds = reactFlowNodes.map((node) => node.id)
  const nodesIdsToDelete = socketNodesIds.filter((id) => !reactFlowNodesIds.includes(id))
  nodesIdsToDelete.forEach((id) => yNodes.delete(id))
}
